import React from 'react';

// mui
import {
  Box,
  CircularProgress,
  styled,
  Typography,
  useTheme,
} from '@mui/material';

// components
import { Link } from 'react-router-dom';
import { Tabs } from '../../../../../components/tabs/v2/TabSubrouter';
import { OrganizationDetailsTab } from './organization-details/OrganizationDetailsTab';
import { PolicyInformationTab } from './policy-information/PolicyInformationTab';
import { CowbellFactorsTab } from './CowbellFactorsTab';

// utils
import { subroutes, tabs } from './policy-details.utils';
import {
  formatForLatestPolicy,
  formatPolicy,
} from '../../../policies.services';
import { useStatusColors } from '../../../../quotes-policies/cards/policyholder/utils';
import { useAccountDetails } from '../../../../../components/hooks/queries/accounts/useAccountDetails';
import { useLatestRiskToggle } from '../../../../../components/hooks/useLatestRiskToggle';
import { useAccountRiskDetails } from '../../../../../components/hooks/useAccountRiskDetails';
import { usePersona } from '../../../../../components/hooks/usePersona';
import {
  shellBasePath,
  accountDetailPath,
} from '../../../../../utils/shell-paths.utils';
import { REDACTED_FIELD } from '../../../../../config/logrocket';
import { useQuoteDetails } from '../../../../../components/hooks/queries/quotes/useQuoteDetails';
import { useSubmissionDetails } from '../../../../../components/hooks/queries/submissions/useSubmissionDetails';
import { SubmissionSourceIcon } from '../../../../../components/icons/SubmissionSourceIcon';
import { PolicyClaimIndicator } from './PolicyClaimIndicator';

export const PolicyDetailsDrawer = ({
  policy = {},
  product,
  getPolicyStatus,
  fetchLatestPolicyByStatus = false,
}: any) => {
  const formattedPolicy = fetchLatestPolicyByStatus
    ? formatForLatestPolicy(policy, product)
    : _formatPolicyForDetailsDrawer(policy);

  const { palette } = useTheme();
  const [selectedTab, setSelectedTab] = React.useState(
    tabs.POLICY_INFORMATION_TAB
  );

  const accountDetailsQuery = useAccountDetails(formattedPolicy.accountId);

  const { data: accountRiskDetails, isLoading: areAccountRiskDetailsLoading } =
    useAccountRiskDetails({
      accountId: formattedPolicy.accountId,
    });

  const handleTabChange = (e: any, nextTab: any) => {
    setSelectedTab(nextTab);
  };

  if (areAccountRiskDetailsLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="13rem"
        width="42rem"
      >
        <CircularProgress size="5rem" />
      </Box>
    );
  }

  return (
    <>
      <PolicyHeader
        policy={formattedPolicy}
        getPolicyStatus={getPolicyStatus}
        product={product}
      />
      <Tabs
        subroutes={subroutes}
        value={selectedTab}
        onChange={handleTabChange}
        variant="fullWidth"
        tabProps={{
          selectedStyles: {
            backgroundColor: palette.background.default,
          },
        }}
      />
      <TabsRouter
        selectedTab={selectedTab}
        account={accountDetailsQuery.data ?? {}}
        policy={formattedPolicy}
        product={product}
        accountRiskDetails={accountRiskDetails}
      />
    </>
  );
};

const TabsRouter = ({
  selectedTab,
  account,
  policy,
  product,
  accountRiskDetails,
}: any) => {
  const latestRisk = useLatestRiskToggle();
  switch (selectedTab) {
    case tabs.POLICY_INFORMATION_TAB:
      return <PolicyInformationTab policy={policy} product={product} />;

    case tabs.ORGANIZATION_DETAILS_TAB:
      return (
        <OrganizationDetailsTab
          policy={policy}
          account={account}
          latestRisk={latestRisk}
        />
      );
    case tabs.COWBELL_FACTORS_TAB:
      return (
        <CowbellFactorsTab
          policy={policy}
          accountRiskDetails={accountRiskDetails}
          latestRisk={latestRisk}
        />
      );
    default:
      return null;
  }
};

const PolicyHeader = ({ policy, getPolicyStatus, product }: any) => {
  const status = getPolicyStatus(policy);
  const statusColor = useStatusColors(status);

  const { isPool } = usePersona();
  const accountBasePath = shellBasePath(isPool);

  // Fetch quote details to get submissionId
  const quoteDetailsQuery = useQuoteDetails({
    id: policy.quoteId,
    product: product || policy.product,
  });

  // Fetch submission details to get submissionSource and msgId
  const submissionId = quoteDetailsQuery.data?.submissionId;
  const submissionDetailsQuery = useSubmissionDetails(submissionId);

  const submissionSource = submissionDetailsQuery.data?.submissionSource;
  const msgId =
    submissionDetailsQuery.data?.metaData?.msgId ||
    submissionDetailsQuery.data?.msgId ||
    undefined;

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      mt={1}
      mb={3}
    >
      <Box mt={1} display="flex" alignItems="center" gap={1}>
        <DrawerHeaderText>
          Policy Number {policy.policyNumber}
          <DrawerHeaderSpan
            style={{ color: statusColor }}
          >{`[${status}]`}</DrawerHeaderSpan>
        </DrawerHeaderText>
        {submissionSource && (
          <SubmissionSourceIcon
            submissionSource={submissionSource}
            msgId={msgId}
          />
        )}
        <PolicyClaimIndicator policy={policy} />
      </Box>
      <Typography
        component={Link}
        to={accountDetailPath(accountBasePath, policy.accountId)}
        className={`${REDACTED_FIELD} link-underlined--hover`}
      >
        {policy.companyName}
      </Typography>
    </Box>
  );
};

const _formatPolicyForDetailsDrawer = (policy: any) => {
  const baseFormattedPolicy = formatPolicy(policy);
  return {
    ...baseFormattedPolicy,
    ...baseFormattedPolicy.latestPolicy,
  };
};

const DrawerHeaderText = styled('h1')(({ theme: { palette, config } }) => ({
  color: palette.primary.main,
  fontWeight: config.weights.normal,
  margin: 0,
  fontSize: config.textSizes.paragon,
}));

const DrawerHeaderSpan = styled('span')(({ theme: { config } }) => ({
  color: 'green',
  fontSize: config.textSizes.greatPrimer,
  marginLeft: '0.75rem',
  textTransform: 'uppercase',
}));
